import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { FixMeLater } from '../../utils';
import { useStyles } from './Typography.styles';
import { Styler, StylerBaseProps } from '../Styler';
import {
    TextColorOption,
    TypographyFamily,
    TypographyWeight,
    useComponentDefaultProps,
    dotToKebab,
    camelToKebab,
    TypographySize,
    TypographyStyleClasses,
} from '../../theming';

type TextCssVariables =
    | '--font-family'
    | '--font-weight'
    | '--text-transform'
    | '--font-size'
    | '--line-height'
    | '--letter-spacing'
    | '--font-weight'
    | '--color';

// Static styles are pre-serialised
const STATIC_STYLES: { [key in TypographyStyleClasses]: SerializedStyles } = {
    root: css`
        margin: 0;
        padding: 0;
        text-underline-offset: 0.125em;

        font-size: var(--font-size);
        line-height: var(--line-height);
        letter-spacing: var(--letter-spacing);
        font-family: var(--font-family);
        font-weight: var(--font-weight);
        text-transform: var(--text-transform);
        color: var(--color);

        &:is(label) {
            display: inline-block;
        }
    `,
};

export interface TypographyBaseProps extends StylerBaseProps {
    /**
     * The root element.
     * @default p
     */
    component?: React.ElementType;

    /**
     * The color of the text
     * @default inherit
     */
    color?: TextColorOption;

    /**
     * If `true`, the italic font style will be applied.
     */
    italic?: boolean;

    /**
     * If `true`, the underline text decoration style will be applied.
     */
    underlined?: boolean;

    /**
     * If `true`, the line-through text decoration style will be applied.
     */
    lineThrough?: boolean;

    /**
     * The text-align css property.
     */
    align?: React.CSSProperties['textAlign'];

    /**
     * The text-transform css property.
     */
    transform?: React.CSSProperties['textTransform'];

    /**
     * If `true`, the text will be truncated with an ellipsis if it overlows.
     */
    truncate?: boolean;

    /**
     * Override the default line height for the chosen size.
     * Either a theme line height or any valid css line-height value.
     */
    lineHeight?: React.CSSProperties['lineHeight'];

    /**
     * The font weight.
     *  @default default
     */
    weight?: TypographyWeight | 'inherit';
}

export interface TypographyProps extends TypographyBaseProps {
    /**
     * The typography family.
     * @default inherit
     */
    family?: TypographyFamily | 'inherit';

    /**
     * The text size.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default inherit
     */
    size?: TypographySize | 'inherit';
}

const defaultProps: Partial<TypographyBaseProps> = {
    family: 'inherit',
    size: 'inherit',
    color: 'inherit',
    weight: 'default',
    italic: false,
    underlined: false,
    lineThrough: false,
    truncate: false,
};

export const Typography: React.ForwardRefExoticComponent<TypographyProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function Typography(
        props: TypographyProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const {
            color = 'inherit',
            weight = 'default',
            size = 'inherit',
            family = 'inherit',
            italic,
            underlined: underline,
            lineThrough,
            align,
            transform,
            truncate,
            lineHeight,
            component,
            ...other
        } = useComponentDefaultProps('Typography', defaultProps, props);

        const { styles: dynamicStyles } = useStyles({
            italic,
            underline,
            lineThrough,
            align,
            transform,
            lineHeight,
        });

        // Prepare style data attributes
        const attributeKeys: Array<string> = [];

        if (family !== 'inherit') {
            attributeKeys.push(`data-family-${family}`);
        }

        if (size !== 'inherit') {
            attributeKeys.push(`data-${family}-${size}`);
        }

        if (truncate) {
            attributeKeys.push(`data-truncate`);
        }

        const cssDataAttributes = Object.fromEntries(attributeKeys.map((key) => [key, true]));

        // Prepare CSS variables
        const cssVariables: { [key in TextCssVariables]: string } = React.useMemo(() => {
            return {
                '--font-family': family === 'inherit' ? 'inherit' : `var(--hc-font-family)`,
                '--text-transform': family === 'inherit' ? 'inherit' : `var(--hc-text-transform)`,
                '--font-weight': weight === 'inherit' ? 'inherit' : `var(--hc-font-weight-${camelToKebab(weight)})`,
                '--font-size': size === 'inherit' ? 'inherit' : `var(--hc-font-size)`,
                '--line-height': size === 'inherit' ? 'inherit' : `var(--hc-line-height)`,
                '--letter-spacing': size === 'inherit' ? 'inherit' : `var(--hc-letter-spacing)`,
                '--color': color === 'inherit' ? 'inherit' : `var(--hc-color-${dotToKebab(color)})`,
            };
        }, [color, family, size, weight]);

        return (
            <Styler
                ref={ref}
                style={cssVariables}
                baseStyles={[STATIC_STYLES.root, css(dynamicStyles.root)]}
                component={component}
                {...cssDataAttributes}
                {...other}
            />
        );
    });
